// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: opens "Ask about this chat" on phones (the Konvo panel's sheet, AskAboutSheet) for a
// Konvo, Teams or WhatsApp chat, one of its messages or an email, puts a drafted reply into that
// chat's composer (or a reply to the email), and starts voice mode from the sparkle's hold. The pure parts are in askAbout.ts.

import Channels from '@app/features/channel/state/Channels';
import AskKonvo from '@app/features/konvo_ask/state/AskKonvo';
import {setAssistantDraft} from '@app/features/konvo_assistant/state/AssistantDraft';
import {openVoiceMode} from '@app/features/konvo_assistant/voice/VoiceModeSession';
import type {KonvoMailMessage} from '@app/features/konvo_mail/state/KonvoMail';
import {aboutContext, type PanelAbout} from '@app/features/konvo_panel/state/askAbout';
import KonvoPanel, {type PanelSource} from '@app/features/konvo_panel/state/KonvoPanel';
import {konvoChatFocus, whatsappChatFocus} from '@app/features/konvo_panel/state/QuickTaskSources';
import KonvoWhatsApp, {mediaLabel, type WhatsAppMessage} from '@app/features/konvo_whatsapp/state/KonvoWhatsApp';
import WhatsAppDrafts from '@app/features/konvo_whatsapp/state/WhatsAppDrafts';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {ComponentBus} from '@app/features/platform/utils/ComponentBus';
import {KONVOBOT_ID} from '@konvo/constants/src/AppConstants';
import {ChannelTypes} from '@konvo/constants/src/ChannelConstants';
import {type I18n, i18n as linguiI18n} from '@lingui/core';

const logger = new Logger('AskAboutChat');

function open(about: PanelAbout, source: PanelSource, page = 'chats'): void {
	KonvoPanel.open({
		starter: 'free',
		context: aboutContext(about) as Record<string, string>,
		about,
		source,
		page,
		run: false,
	});
}

/** A Konvo or Teams chat (by channel id), or one message in it. False when the chat is not known. */
export function askAboutKonvoChat(channelId: string, message?: Message | null): boolean {
	const chat = konvoChatFocus(channelId);
	if (!chat) return false;
	const channel = Channels.getChannel(channelId);
	const recipientId = channel?.type === ChannelTypes.DM ? channel.recipientIds[0] : undefined;
	const about: PanelAbout = {
		chat,
		target: {kind: 'konvo', channelId},
		avatar: {userId: message ? message.author.id : recipientId},
	};
	if (message) {
		about.message = {
			id: message.id,
			author: message.author.globalName ?? message.author.username,
			text: message.content ?? '',
		};
	}
	open(about, message ? 'message_menu' : 'composer');
	return true;
}

/** A WhatsApp chat, or one message in it. False when the chat is not known. */
export function askAboutWhatsAppChat(jid: string, message?: WhatsAppMessage | null): boolean {
	const chat = whatsappChatFocus(jid);
	if (!chat) return false;
	const entry = KonvoWhatsApp.chats.find((item) => item.jid === jid);
	const about: PanelAbout = {
		chat,
		target: {kind: 'whatsapp', jid},
		avatar: {whatsappJid: jid, whatsappPicture: entry?.has_picture ?? false},
	};
	if (message) {
		const author = message.from_me ? 'me' : (message.sender_name ?? chat.name);
		about.message = {
			id: message.id,
			author,
			text: message.text.trim() || mediaLabel(message.media_kind, message.media_name, message.media_gif),
		};
		if (!message.from_me && message.sender_jid && chat.group) {
			about.avatar = {whatsappJid: message.sender_jid, whatsappPicture: true};
		}
	}
	open(about, message ? 'message_menu' : 'composer');
	return true;
}

/** One email (Ask Konvo on a mail's menu), as a chat with its sender. */
export function askAboutMail(message: KonvoMailMessage, page = 'chats'): void {
	const name = message.from_name || message.from_address || '';
	open(
		{
			chat: {
				source: 'mail',
				conversationId: `mail:${message.conversation_id ?? message.id}`,
				name,
				group: false,
				unread: message.is_read ? 0 : 1,
				lastFromThem: true,
			},
			target: {kind: 'mail', messageId: message.id},
			avatar: {},
			mail: {
				id: message.id,
				conversationId: message.conversation_id ?? null,
				subject: message.subject,
				fromAddress: message.from_address ?? '',
			},
		},
		'message_menu',
		page,
	);
}

/**
 * "Put in reply": the drafted text replaces what the chat's composer holds; the person sends it.
 * A Konvo composer that is not mounted (the chat was left) finds it as the chat's draft. An email
 * gets a reply (to everyone on it, as the main reply goes) holding the text.
 */
export function putReplyInComposer(about: PanelAbout, text: string): void {
	const target = about.target;
	if (target.kind === 'mail') {
		void import('@app/features/konvo_mail/components/EmailsPage').then(({openReplyWithText}) =>
			openReplyWithText(linguiI18n, target.messageId, text),
		);
		return;
	}
	if (target.kind === 'whatsapp') {
		WhatsAppDrafts.set(target.jid, text);
		return;
	}
	const taken = ComponentBus.dispatchToFirstResult(
		'TEXTAREA_PUT_TEXT',
		{channelId: target.channelId, text},
		(result) => result === true,
	);
	if (taken !== true) setAssistantDraft(target.channelId, text);
}

/** The sparkle's hold: voice mode with Konvo. Starts from the gesture when the Assistant DM is known. */
export function startKonvoVoice(i18n: I18n): void {
	const known =
		AskKonvo.channelId ??
		Channels.getPrivateChannels().find(
			(channel) => channel.type === ChannelTypes.DM && channel.recipientIds.includes(KONVOBOT_ID),
		)?.id;
	if (known) {
		openVoiceMode(known, i18n);
		return;
	}
	void AskKonvo.ensureChannel()
		.then((channelId) => {
			if (channelId) openVoiceMode(channelId, i18n);
		})
		.catch((error) => logger.warn('No Assistant DM for voice mode', error));
}
